import { useEffect, useRef } from 'react'

import { AxiosError } from 'axios'

import { getCurrentUser, refreshToken } from 'src/app/auth/adapters/knap'
import { mapKnapUser } from 'src/app/auth/domain/use-cases/map-knap-user'
import { useAuthStore } from 'src/app/auth/store/auth-store'
import { AUTH_SESSION_EXPIRED_CODE } from 'src/interfaces/http/errors'

let activeRestoreOwner: symbol | undefined

export const useSessionRestore = (): void => {
  const restoreStatus = useAuthStore(state => state.restoreStatus)
  const setCurrentUser = useAuthStore(state => state.setCurrentUser)
  const setPendingToken = useAuthStore(state => state.setPendingToken)
  const setSessionExpired = useAuthStore(state => state.setSessionExpired)
  const setRestoreStatus = useAuthStore(state => state.setRestoreStatus)
  const mountedRef = useRef(true)
  const attemptRef = useRef(0)
  const restoreOwnerRef = useRef<symbol>()

  useEffect(() => {
    mountedRef.current = true
    return (): void => {
      mountedRef.current = false
      attemptRef.current += 1
      const restoreOwner = restoreOwnerRef.current
      if (restoreOwner && activeRestoreOwner === restoreOwner) {
        activeRestoreOwner = undefined
        setPendingToken(null)
        if (useAuthStore.getState().restoreStatus === 'restoring') {
          setRestoreStatus('idle')
        }
      }
      restoreOwnerRef.current = undefined
    }
  }, [setPendingToken, setRestoreStatus])

  useEffect(() => {
    if (restoreStatus !== 'idle') return

    const attempt = ++attemptRef.current
    const restoreOwner = Symbol('session-restore')
    activeRestoreOwner = restoreOwner
    restoreOwnerRef.current = restoreOwner
    const initialUser = useAuthStore.getState().currentUser

    const isActive = (): boolean =>
      mountedRef.current &&
      attemptRef.current === attempt &&
      activeRestoreOwner === restoreOwner

    const finishAttempt = (): void => {
      if (activeRestoreOwner === restoreOwner) {
        activeRestoreOwner = undefined
      }
      if (restoreOwnerRef.current === restoreOwner) {
        restoreOwnerRef.current = undefined
      }
    }

    const authStateChanged = (): boolean =>
      useAuthStore.getState().currentUser !== initialUser

    setRestoreStatus('restoring')

    const restore = async (): Promise<void> => {
      try {
        const token = await refreshToken()
        if (!isActive()) return
        if (authStateChanged()) {
          finishAttempt()
          setRestoreStatus('done')
          return
        }

        setPendingToken(token)
        const dto = await getCurrentUser()
        if (!isActive()) return
        if (authStateChanged()) {
          finishAttempt()
          setRestoreStatus('done')
          return
        }

        const activeToken = useAuthStore.getState().pendingToken
        if (!activeToken) {
          finishAttempt()
          setRestoreStatus('done')
          return
        }

        const user = mapKnapUser({ dto })
        finishAttempt()
        setCurrentUser({
          token: activeToken,
          data: user,
          provider: user.provider ?? 'google',
        })
        setRestoreStatus('done')
      } catch (error) {
        if (!isActive()) return

        finishAttempt()
        setPendingToken(null)
        if (authStateChanged()) {
          setRestoreStatus('done')
          return
        }

        if (
          error instanceof AxiosError &&
          error.code === AUTH_SESSION_EXPIRED_CODE
        ) {
          setCurrentUser(null)
          if (initialUser) {
            setSessionExpired(true)
          }
          setRestoreStatus('done')
          return
        }

        setRestoreStatus(initialUser ? 'retryable-error' : 'done')
      }
    }

    void restore()
  }, [
    restoreStatus,
    setCurrentUser,
    setPendingToken,
    setRestoreStatus,
    setSessionExpired,
  ])
}
